import { useState } from 'react';
import { Card, Steps } from '@bancobarigui/barihaus';
import { Spin } from 'antd';
import { pdf } from '@react-pdf/renderer';

import { InspectionRequest } from '@/services/types/inspection.types';
import { LaudoWizardData, WizardMode, WizardReviewContext } from '../../types/laudo-wizard.types';
import { useLaudoWizard } from './useLaudoWizard';
import { useStepRenderer } from './useStepRenderer';
import { WizardNavigation } from './WizardNavigation';
import { LaudoStep } from '../../types/laudo-wizard.types';
import { LaudoDocument } from '../../pdf/LaudoDocument';
import { docmanService } from '@/services/docman.service';
import { inspectionReportService } from '@/services/inspectionReport.service';
import { inspectionReportReviewService } from '@/services/inspectionReportReview.service';
import { inspectionAssignmentService } from '@/services/inspectionAssignment.service';
import { inspectionService } from '@/services/inspection.service';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { styles, stepsStyles } from './styles';
import { FieldCorrection } from '@/shared/types/inspectionReportReview.types';
import { createContext, useContext } from 'react';
import { useAlertContext } from '@/contexts/alert.context';
import { useWizardDraft } from '../../hooks/useWizardDraft';
import { LIQUIDEZ_OBRIGATORIA_MENSAGEM, parseLiquidez } from '../../utils/liquidez';

interface ReviewModeContextType {
  mode: WizardMode;
  reviewContext?: WizardReviewContext;
  selectedCorrections: FieldCorrection[];
  addCorrection: (correction: FieldCorrection) => void;
  removeCorrection: (fieldPath: string) => void;
  isFieldMarked: (fieldPath: string) => boolean;
}

const ReviewModeContext = createContext<ReviewModeContextType | null>(null);

export const useReviewMode = () => {
  const context = useContext(ReviewModeContext);
  if (!context) {
    return {
      mode: 'create' as WizardMode,
      selectedCorrections: [],
      addCorrection: () => {},
      removeCorrection: () => {},
      isFieldMarked: () => false,
    };
  }
  return context;
};

interface LaudoWizardProps {
  inspection: InspectionRequest;
  initialData?: LaudoWizardData;
  editingReportId?: string;
  mode?: WizardMode;
  reviewContext?: WizardReviewContext;
  onClose?: (wasTransmitted?: boolean) => void;
  onReviewComplete?: (corrections: FieldCorrection[], generalComments?: string) => void;
  hasCompletedVideoCall?: boolean;
  checklistOnlyMode?: boolean;
  isAutomaticReport?: boolean;
  reportCreatedBy?: string;
  /** Edição de laudo já aceito pelo banco: ao transmitir, o PDF sobe na hora e o item permanece aceito. */
  postApprovalEdit?: boolean;
}

const getStepsConfig = (hasCompletedVideoCall: boolean, checklistPreenchido: boolean, mode: WizardMode = 'create', checklistOnlyMode: boolean = false, isAutomaticReport: boolean = false): { title: string }[] => {
  const steps: { title: string }[] = [];

  if (checklistOnlyMode) {
    steps.push({ title: 'Checklist' });
    return steps;
  }

  if (mode === 'editAutomatic') {
    steps.push(
      { title: 'Identificação' },
      { title: 'Fachada' },
      { title: 'Avaliação' },
      { title: 'Metodologia' },
      { title: 'Assinatura' },
      { title: 'Salvar' }
    );
    return steps;
  }

  if (mode === 'review' && isAutomaticReport) {
    steps.push(
      { title: 'Identificação' },
      { title: 'Fachada' },
      { title: 'Avaliação' },
      { title: 'Metodologia' },
      { title: 'Resumo' }
    );
    return steps;
  }

  if (mode === 'calculations') {
    steps.push(
      { title: 'Identificação' },
      { title: 'Vistoria' },
      { title: 'Avaliação' },
      { title: 'Cálculos' },
      { title: 'Assinatura' },
      { title: 'Transmissão' }
    );
    return steps;
  }

  if (hasCompletedVideoCall && !checklistPreenchido) {
    steps.push({ title: 'Checklist' });
  }

  steps.push(
    { title: 'Identificação' },
    { title: 'Vistoria' },
    { title: 'Avaliação' },
    { title: 'Metodologia' }
  );

  if (mode !== 'review') {
    steps.push({ title: 'Assinatura' });
  }

  steps.push({ title: mode === 'review' ? 'Resumo' : 'Transmissão' });

  return steps;
};

export function LaudoWizard(props: LaudoWizardProps) {
  const { checklistOnlyMode = false, mode = 'create', inspection } = props;
  const showDraftButton = !checklistOnlyMode && (mode === 'create' || mode === 'correction' || mode === 'calculations' || mode === 'edit');
  const wizardDraft = useWizardDraft(showDraftButton ? inspection.items?.[0]?.id : undefined);

  // Aguardar carregamento do draft antes de renderizar o wizard
  // Isso garante que useLaudoWizard receba o draftData no primeiro render
  if (showDraftButton && !wizardDraft.draftLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }

  return <LaudoWizardInner {...props} wizardDraft={wizardDraft} showDraftButton={showDraftButton} />;
}

function LaudoWizardInner({
  inspection,
  initialData,
  editingReportId,
  mode = 'create',
  reviewContext,
  onClose,
  onReviewComplete,
  hasCompletedVideoCall = false,
  checklistOnlyMode = false,
  isAutomaticReport = false,
  reportCreatedBy,
  postApprovalEdit = false,
  wizardDraft,
  showDraftButton,
}: LaudoWizardProps & { wizardDraft: ReturnType<typeof useWizardDraft>; showDraftButton: boolean }) {
  const [isTransmitting, setIsTransmitting] = useState(false);
  const [selectedCorrections, setSelectedCorrections] = useState<FieldCorrection[]>([]);
  const { notify } = useAlertContext();
  const { hasAnyProviderRole } = usePermissions();
  const isProviderUser = hasAnyProviderRole();

  const {
    currentStep,
    laudoData,
    goToNext,
    goToPrevious,
    goToStep,
    canGoNext,
    canGoPrevious,
    updateChecklist,
    updateIdentificacao,
    updateVistoria,
    updateAvaliacao,
    updateAmostras,
    updateSignature,
    updateTransmissao,
    signatureDataLoaded,
    skipCalculations: skipCalculationsFn,
  } = useLaudoWizard({
    inspection,
    ...(initialData && { initialData }),
    hasCompletedVideoCall,
    mode,
    checklistOnlyMode,
    draftData: wizardDraft.draftResult ?? undefined,
    isAutomaticReport,
    ...(reportCreatedBy && { reportCreatedBy }),
  });

  const addCorrection = (correction: FieldCorrection) => {
    setSelectedCorrections(prev => [...prev, correction]);
  };

  const removeCorrection = (fieldPath: string) => {
    setSelectedCorrections(prev => prev.filter(c => c.fieldPath !== fieldPath));
  };

  const isFieldMarked = (fieldPath: string) => {
    return selectedCorrections.some(c => c.fieldPath === fieldPath);
  };

  const reviewModeValue: ReviewModeContextType = {
    mode,
    reviewContext,
    selectedCorrections,
    addCorrection,
    removeCorrection,
    isFieldMarked,
  };

  const checklistPreenchido = laudoData.checklist.checklistPreenchido;

  const getDisplayStep = () => {
    if (mode === 'review' && isAutomaticReport) {
      switch (currentStep) {
        case LaudoStep.IDENTIFICACAO: return 0;
        case LaudoStep.VISTORIA: return 1;
        case LaudoStep.AVALIACAO: return 2;
        case LaudoStep.AMOSTRAS: return 3;
        case LaudoStep.TRANSMISSAO: return 4;
        default: return 0;
      }
    }

    if (mode === 'editAutomatic') {
      switch (currentStep) {
        case LaudoStep.IDENTIFICACAO: return 0;
        case LaudoStep.VISTORIA: return 1;
        case LaudoStep.AVALIACAO: return 2;
        case LaudoStep.AMOSTRAS: return 3;
        case LaudoStep.ASSINATURA: return 4;
        case LaudoStep.TRANSMISSAO: return 5;
        default: return 0;
      }
    }

    if (mode === 'calculations') {
      // No modo calculations: Identificação(0), Vistoria(1), Avaliação(2), Cálculos(3), Assinatura(4), Transmissão(5)
      switch (currentStep) {
        case LaudoStep.IDENTIFICACAO: return 0;
        case LaudoStep.VISTORIA: return 1;
        case LaudoStep.AVALIACAO: return 2;
        case LaudoStep.AMOSTRAS: return 3;
        case LaudoStep.ASSINATURA: return 4;
        case LaudoStep.TRANSMISSAO: return 5;
        default: return 0;
      }
    }

    let displayStep = currentStep;

    if (!hasCompletedVideoCall) {
      displayStep = currentStep - 1;
    } else if (checklistPreenchido && currentStep > LaudoStep.CHECKLIST) {
      displayStep = currentStep - 1;
    }

    if (mode === 'review' && currentStep >= LaudoStep.ASSINATURA) {
      displayStep = displayStep - 1;
    }

    return displayStep;
  };

  const visualStepToEnumStep = (visualStep: number): LaudoStep => {
    if (mode === 'review' && isAutomaticReport) {
      switch (visualStep) {
        case 0: return LaudoStep.IDENTIFICACAO;
        case 1: return LaudoStep.VISTORIA;
        case 2: return LaudoStep.AVALIACAO;
        case 3: return LaudoStep.AMOSTRAS;
        case 4: return LaudoStep.TRANSMISSAO;
        default: return LaudoStep.IDENTIFICACAO;
      }
    }

    let enumStep = visualStep;

    if (!hasCompletedVideoCall) {
      enumStep = visualStep + 1;
    } else if (checklistPreenchido) {
      enumStep = visualStep + 1;
    }

    if (mode === 'review' && enumStep >= LaudoStep.ASSINATURA) {
      enumStep = enumStep + 1;
    }

    return enumStep as LaudoStep;
  };

  const displayStep = getDisplayStep();

  const handleChecklistCompleted = () => {
    notify({
      type: 'success',
      message: 'Checklist enviado para avaliação. A Prestadora irá gerar o laudo.',
    });
    setTimeout(() => {
      onClose?.(true);
    }, 1500);
  };

  const { renderStep } = useStepRenderer({
    currentStep,
    laudoData,
    inspection,
    signatureDataLoaded,
    hasCompletedVideoCall,
    goToNext,
    updateChecklist,
    updateIdentificacao,
    updateVistoria,
    updateAvaliacao,
    updateAmostras,
    updateSignature,
    updateTransmissao,
    mode,
    reviewContext,
    checklistOnlyMode,
    onChecklistCompleted: handleChecklistCompleted,
    onSkipCalculations: skipCalculationsFn,
  });

  const handleSaveDraft = async () => {
    await wizardDraft.saveDraft(laudoData, currentStep);
  };

  const handleFinish = async () => {
    if (!laudoData.transmissao.processedLaudo) {
      notify({
        type: 'error',
        message: 'PDF ainda não foi gerado. Aguarde...',
      });
      return;
    }

    // Laudo automático (AVM) não tem step de Checklist, então não exige questionário/fotos.
    // Cobre tanto a geração inicial (mode='editAutomatic') quanto a correção de uma
    // devolução de laudo automático (mode='correction' com isAutomaticReport=true).
    if (mode !== 'editAutomatic' && !isAutomaticReport) {
      const questionarioCount = laudoData.transmissao.processedLaudo?.vistoriaImovel?.questionario?.length ?? 0;
      const fotosCount = laudoData.transmissao.processedLaudo?.relatorioFotografico?.listaFotos?.length ?? 0;
      if (questionarioCount === 0 && fotosCount === 0) {
        notify({
          type: 'error',
          message: 'Não é possível finalizar: o checklist precisa ser preenchido antes de gerar o laudo. Volte ao step "Checklist" e preencha as perguntas.',
        });
        return;
      }
    }

    // O step de Avaliação já exige a liquidez, mas um rascunho salvo antes da lista fechada pode
    // reabrir direto na Transmissão; a trava final garante que nenhum laudo sai sem uma opção válida.
    const liquidez = parseLiquidez(laudoData.avaliacao.dadosImovel.fatorLiquidez);
    if (!liquidez) {
      notify({ type: 'error', message: LIQUIDEZ_OBRIGATORIA_MENSAGEM });
      return;
    }

    console.info('[LaudoWizard:handleFinish]', {
      mode,
      inspectionItemId: inspection.items?.[0]?.id,
      inspectionRequestId: inspection.id,
      questionarioCount: laudoData.transmissao.processedLaudo?.vistoriaImovel?.questionario?.length ?? 0,
      fotosCount: laudoData.transmissao.processedLaudo?.relatorioFotografico?.listaFotos?.length ?? 0,
    });

    setIsTransmitting(true);

    try {
      // Detecta se é correção de devolução externa que será auto-aprovada pelo banco.
      // Auto-aprovação só vale quando o PRÓPRIO BANCO está corrigindo uma devolução do
      // sistema externo (status 21). Quando a PRESTADORA corrige (inclusive devolução
      // pós-aceite, status 30), o laudo precisa voltar pra revisão do banco — não aceitar
      // automaticamente. O reviewerType no contexto indica quem fez a devolução, não quem
      // está corrigindo agora; por isso a checagem precisa ser !isProviderUser.
      const isExternalReturnCorrection = mode === 'correction' && reviewContext?.externalReturn !== undefined;
      const isAutoApprovedByBank = isExternalReturnCorrection && !isProviderUser;

      // Política: o PDF só vai pro bucket quando o Banco APROVAR o laudo de fato.
      // O único caso em que o handleFinish do wizard sobe direto é a auto-aprovação
      // por devolução externa pelo Banco (equivale a uma aprovação manual).
      // Demais modos (create, correction normal, calculations, editAutomatic, edit) → apenas metadata.
      // O upload acontece quando o Banco clicar em "Aprovar sem Correções" no ReviewReportModal.
      // NOTA (AVM/AguardandoPreenchimentoBanco): a transmissão do banco vai para
      // AguardandoRevisaoBanco (o backend só aceitaria direto se o PDF já viesse anexado);
      // o aceite + upload do PDF acontecem na aprovação, como nos demais fluxos.
      // EXCEÇÃO (postApprovalEdit): edição de laudo já aceito — o item permanece aceito
      // e não haverá etapa de aprovação, então o PDF precisa subir agora (sem DocumentId
      // o backend rebaixaria o item para AguardandoRevisaoBanco).
      const shouldUploadToBucket = isAutoApprovedByBank || postApprovalEdit;

      const pdfDocument = <LaudoDocument
        data={laudoData.transmissao.processedLaudo}
        logoBase64={laudoData.transmissao.logoBase64}
        isAutomatic={mode === 'editAutomatic'}
        streetViewImageBase64={laudoData.transmissao.streetViewImageBase64}
      />;

      let uploadResult: { documentId: string } = { documentId: '' };
      let pdfFile: File | null = null;

      const itemId = inspection.items?.[0]?.id;
      if (!itemId) {
        throw new Error('ID do item de inspeção não encontrado');
      }

      if (shouldUploadToBucket) {
        const pdfBlob = await pdf(pdfDocument).toBlob();
        pdfFile = new File(
          [pdfBlob],
          `laudo-${inspection.proposalNumber}-${Date.now()}.pdf`,
          { type: 'application/pdf' }
        );

        uploadResult = await docmanService.uploadFile(
          itemId,
          pdfFile,
          'LAUDO_AVALIACAO',
        );
      }

      let fotoFachadaDocumentId: string | undefined = laudoData.vistoria.fotoFachadaDocumentId;

      const fotoFachada = laudoData.vistoria.fotoFachada;
      // Sobe foto nova se for base64 sem documentId associado (anexada agora ou rotacionada).
      // Foto carregada do backend via downloadFile vira base64 mas preserva o documentId — reusa.
      const isNewBase64Photo = !!fotoFachada
        && fotoFachada.startsWith('data:')
        && !fotoFachadaDocumentId;

      if (isNewBase64Photo) {
        const base64Data = fotoFachada.split(',')[1] || fotoFachada;
        const byteString = atob(base64Data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([uint8Array], { type: 'image/jpeg' });
        const fotoFile = new File([blob], `foto-fachada-${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Falha aqui ABORTA a transmissão: sem o documentId o laudo seria salvo
        // sem referência da foto coletada, e o PDF re-gerado na aprovação (e o
        // Salesforce) exibiriam outra imagem no lugar (fallback do checklist).
        let uploadError: unknown;
        for (let tentativa = 1; tentativa <= 2 && !fotoFachadaDocumentId; tentativa++) {
          try {
            const fotoUploadResult = await docmanService.uploadFile(
              itemId,
              fotoFile,
              'FOTOS_IMOVEL',
            );
            fotoFachadaDocumentId = fotoUploadResult.documentId;
          } catch (err) {
            uploadError = err;
          }
        }

        if (!fotoFachadaDocumentId) {
          console.error('[fotoFachada] Falha no upload:', uploadError);
          throw new Error('Não foi possível enviar a foto de fachada. Verifique a conexão e transmita novamente.');
        }
      }

      // Sem foto anexada no wizard: o preview transmitido usou uma foto do
      // checklist como fachada (fallback do transform). Persiste a referência
      // DESSA foto para o PDF re-gerado na aprovação e o Salesforce exibirem
      // exatamente a mesma imagem que o inspetor viu ao transmitir.
      if (!fotoFachadaDocumentId) {
        fotoFachadaDocumentId = laudoData.transmissao.processedLaudo?.fotoFachadaDocumentId;
      }

      // 4. Salvar o report no backend com os dados do wizard
      // Preparar wizardData para salvamento (remover dados binários pesados)
      // isAutomaticReport=false aqui sempre: toda gravação via wizard é uma transmissão
      // humana (mesmo em mode='editAutomatic'). Só o AI service grava true (na geração inicial).
      const wizardDataToSave = {
        version: '1.0',
        isAutomaticReport: false,
        wizardData: {
          ...laudoData,
          amostras: {
            ...laudoData.amostras,
            // Limpar imagens base64 das amostras (já estão no PDF)
            amostras: laudoData.amostras.amostras?.map(a => ({
              ...a,
              imagemUrl: a.imagemUrl?.startsWith('data:') ? undefined : a.imagemUrl,
            })),
            // Se modo calculations, os cálculos foram preenchidos — remove o flag
            ...(mode === 'calculations' ? { skipCalculations: false } : {}),
          },
          avaliacao: {
            ...laudoData.avaliacao,
            // Grava sempre a grafia canônica ("Rápida", com acento): a integração repassa o texto como está.
            dadosImovel: { ...laudoData.avaliacao.dadosImovel, fatorLiquidez: liquidez },
            _initialized: true,
          },
          vistoria: {
            checklistId: laudoData.vistoria.checklistId,
            dataVistoria: laudoData.vistoria.dataVistoria,
            vistoriador: laudoData.vistoria.vistoriador,
            fotoFachadaDocumentId: fotoFachadaDocumentId,
            fotoFachadaCaption: laudoData.vistoria.fotoFachadaCaption,
            checklistRespostas: {},
            processedResponses: undefined,
            fotoFachada: undefined,
          },
          signature: {
            evaluatorName: laudoData.signature.evaluatorName,
            creaCauNumber: laudoData.signature.creaCauNumber,
            signatureImage: laudoData.signature.signatureImage,
          },
          transmissao: {
            ...laudoData.transmissao,
            processedLaudo: undefined,
            logoBase64: undefined,
          },
        },
        generatedAt: new Date().toISOString(),
        inspection: {
          id: inspection.id,
          proposalNumber: inspection.proposalNumber,
        },
      };

      let reportNotes = 'Laudo gerado via wizard';
      if (mode === 'correction') {
        const reviewerLabel = reviewContext?.reviewerType === 'Provider' ? 'solicitadas pela Prestadora'
          : reviewContext?.reviewerType === 'Bank' ? 'solicitadas pelo Banco' : 'solicitadas';

        if (isExternalReturnCorrection) {
          reportNotes = `Laudo corrigido após devolução externa - ${reviewerLabel}`;
        } else {
          reportNotes = `Laudo corrigido pelo inspetor - Correções aplicadas (${reviewerLabel})`;
        }

        // Só tenta marcar correções como resolvidas se NÃO for devolução externa
        // (devolução externa não tem fieldCorrections para resolver)
        if (reviewContext?.reportId && !isExternalReturnCorrection && reviewContext?.pendingCorrections && reviewContext.pendingCorrections.length > 0) {
          try {
            await inspectionReportService.markCorrectionsAsResolved(reviewContext.reportId);
          } catch {
            // Continua mesmo se falhar, pois o novo laudo está sendo criado
          }
        }
      } else if (mode === 'calculations') {
        reportNotes = 'Cálculos preenchidos e transmitidos';
      } else if (mode === 'edit') {
        reportNotes = 'Laudo editado';
      } else if (mode === 'editAutomatic') {
        reportNotes = 'Laudo automático editado manualmente';
      }

      if (postApprovalEdit) {
        reportNotes = 'Laudo editado e retransmitido após o aceite do banco';
      }

      // editAutomatic: sincronizar área editada com InspectionItem antes de criar o Report.
      // Sem isso, InspectionItem.PrivateArea/BuiltArea/LandArea ficam com valores antigos
      // e o reload do wizard (que lê de firstItem.privateArea) mostra a área anterior.
      // Falha aqui não bloqueia o save do report — só loga.
      if (mode === 'editAutomatic') {
        try {
          await inspectionService.updateItemPropertyData(itemId, {
            privateArea: laudoData.identificacao.areaPrivativa,
            builtArea: laudoData.avaliacao.dadosImovel.areaConstruida,
            landArea: laudoData.identificacao.areaTotal,
          });
        } catch (err) {
          console.warn('[editAutomatic] Falha ao sincronizar property-data:', err);
        }
      }

      const createdReport = await inspectionReportService.createReport({
        inspectionItemId: itemId,
        documentId: uploadResult.documentId,
        fileName: pdfFile?.name ?? '',
        fileSize: pdfFile?.size ?? 0,
        fileType: 'application/pdf',
        filePath: uploadResult.documentId ? `docman/${uploadResult.documentId}` : '',
        reportData: JSON.stringify(wizardDataToSave),
        notes: reportNotes,
        isAutomaticReport: false,
      });

      // Se estiver em modo calculations e for prestadora, submeter cálculos (muda status 29 → 15)
      if (mode === 'calculations' && isProviderUser) {
        try {
          await inspectionAssignmentService.submitCalculations(itemId, {
            notes: 'Cálculos preenchidos pela prestadora',
          });
        } catch {
          // Continua mesmo se falhar — o report já foi criado
        }
      }

      // Auto-aprovação só dispara quando o próprio banco corrige uma devolução externa.
      // Se a prestadora estiver corrigindo (mesmo após devolução pós-aceite), o laudo
      // segue pra revisão do banco — não aceita automaticamente.
      if (mode === 'correction' && isAutoApprovedByBank) {
        try {
          await inspectionReportReviewService.approveReport(
            createdReport.id,
            'Bank',
            {
              generalComments: 'Laudo corrigido e aprovado automaticamente pelo banco após devolução externa',
            }
          );
        } catch {
          // Continua mesmo se falhar na aprovação automática
        }
      }

      // 4. Atualizar status de transmissão
      updateTransmissao({
        transmissao: {
          ...laudoData.transmissao.transmissao,
          dataTransmissao: new Date().toISOString(),
          statusTransmissao: 'enviado',
        },
      });

      // Limpar rascunho apos transmissao bem-sucedida
      wizardDraft.clearDraft().catch(() => {});

      const successMsg = postApprovalEdit
        ? 'Laudo retransmitido com sucesso!'
        : mode === 'editAutomatic'
          ? 'Laudo salvo com sucesso!'
          : mode === 'calculations'
            ? 'Cálculos transmitidos com sucesso!'
            : 'Laudo transmitido com sucesso!';

      notify({
        type: 'success',
        message: successMsg,
      });

      // 5. Fechar wizard após 1.5s (passando true para indicar que foi transmitido)
      setTimeout(() => {
        onClose?.(true);
      }, 1500);

    } catch (error: any) {
      notify({
        type: 'error',
        message: `Erro ao transmitir laudo: ${error.message || 'Erro desconhecido'}`,
      });
      setIsTransmitting(false);
    }
  };

  const getTitle = () => {
    if (checklistOnlyMode) {
      return 'Preenchimento do Checklist';
    }
    switch (mode) {
      case 'review':
        return `Revisão do Laudo - ${reviewContext?.reviewerType === 'Provider' ? 'Prestadora' : 'Banco'}`;
      case 'correction':
        return 'Correção do Laudo';
      case 'edit':
        return 'Edição do Laudo';
      case 'editAutomatic':
        return 'Edição do Laudo Automático';
      case 'calculations':
        return 'Preenchimento de Cálculos';
      default:
        return 'Geração do Laudo de Avaliação';
    }
  };

  return (
    <ReviewModeContext.Provider value={reviewModeValue}>
      <div className="laudo-wizard-container" style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>{getTitle()}</h3>
          {mode === 'review' && (
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              Clique nos campos que necessitam correção. {selectedCorrections.length} campo(s) marcado(s).
            </p>
          )}
        </div>

        {/* Exibir informações de devolução externa quando estiver em modo correction e houver dados */}
        {mode === 'correction' && reviewContext?.externalReturn && (
          <Card style={{ marginBottom: 16}}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: 16,
                  fontWeight: 600
                }}>
                  Laudo Devolvido pelo Sistema Externo
                </h3>
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: '#8c8c8c', fontSize: 13 }}>Motivo:</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#262626' }}>
                    {reviewContext.externalReturn.reason}
                  </p>
                </div>
                {reviewContext.externalReturn.description && (
                  <div>
                    <strong style={{ color: '#8c8c8c', fontSize: 13 }}>Descrição:</strong>
                    <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#262626', whiteSpace: 'pre-wrap' }}>
                      {reviewContext.externalReturn.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        <div style={styles.stepsContainer} className="laudo-wizard-steps">
          <style>{stepsStyles}</style>
          <Steps
            current={displayStep}
            {...(mode === 'review' ? { onChange: (step) => goToStep(visualStepToEnumStep(step)) } : {})}
            items={getStepsConfig(hasCompletedVideoCall, checklistPreenchido, mode, checklistOnlyMode, isAutomaticReport).map((step) => ({
              title: step.title,
              disabled: mode !== 'review',
            }))}
          />
        </div>

        <Card style={styles.stepContent}>{renderStep()}</Card>

        {!checklistOnlyMode && (
          <WizardNavigation
            currentStep={currentStep}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            pdfGenerated={laudoData.transmissao.pdfGerado}
            processedLaudo={laudoData.transmissao.processedLaudo}
            logoBase64={laudoData.transmissao.logoBase64}
            streetViewImageBase64={laudoData.transmissao.streetViewImageBase64}
            processingUrls={laudoData.transmissao.processingUrls}
            isTransmitting={isTransmitting}
            onNext={goToNext}
            onPrevious={goToPrevious}
            onFinish={mode === 'review' ? () => onReviewComplete?.(selectedCorrections, laudoData.transmissao.generalComments) : handleFinish}
            mode={mode}
            onSaveDraft={showDraftButton ? handleSaveDraft : undefined}
            savingDraft={wizardDraft.saving}
          />
        )}
      </div>
    </ReviewModeContext.Provider>
  );
}
